import React from "react"

import {
  getCustomerCompanyListQueryKey,
  useCustomerCompanyCreate,
} from "@boundless/bl-api-client/customer/services"
import { CustomerCompanyCreateBody } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { PlusIcon } from "lucide-react"
import { useForm } from "react-hook-form"

import { FormInput } from "@/components/form-elements/FormInput"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { store } from "@/lib/store"

import type { z } from "zod"

const AddCompanySchema = CustomerCompanyCreateBody.pick({ legalName: true })

type AddCompanySchema = z.infer<typeof AddCompanySchema>

/**
 * Creates a company from its legal name alone and switches the dashboard to
 * it, landing the user on its first onboarding step.
 */
export function AddCompanyDialog() {
  const [open, setOpen] = React.useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const form = useForm<AddCompanySchema>({
    resolver: zodResolver(AddCompanySchema),
    defaultValues: {
      legalName: "",
    },
  })

  const { mutate: createCompany, isPending } = useCustomerCompanyCreate({
    mutation: {
      onSuccess: (company) => {
        // Store first, then invalidate: the refetch that follows runs
        // `resolveActiveCompanyUuid`, which keeps the new uuid only if the
        // store already holds it. `/resume` then derives the step from it,
        // the same way the header switcher lands a switch.
        store.setState({ activeCompanyUuid: company.uuid })
        queryClient.invalidateQueries({
          queryKey: getCustomerCompanyListQueryKey(),
        })
        navigate({ to: "/resume" })
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon />
          Add Company
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a Company</DialogTitle>
          <DialogDescription>
            You will be taken to the new company to fill in its details.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit((data) => createCompany({ data }))}
          >
            <FormInput name="legalName" placeholder="Legal Business Name" />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              {isPending ? (
                <LoadingButton />
              ) : (
                <Button type="submit">Create</Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
